import { type useFormatter } from '@/composables';
import { isTruthy } from '@/utils/filters';
import { roundDurationToHours } from '@/utils/time';
import {
	type YAxisOptions,
	type AxisLabelsFormatterContextObject,
	type Options,
	type AccessibilityOptions,
	type PlotOptions,
	type TooltipOptions,
	type TooltipFormatterContextObject,
	type ChartOptions,
	addEvent,
	type Series,
	type PlotLineOrBand,
	type LegendOptions,
	type AxisLabelsFormatterCallbackFunction,
	type ASTNode,
	type DefsOptions,
} from 'highcharts';
import partition from 'lodash/partition';
import merge from 'lodash/merge';

import './highchartsPlugins';
import { useMainStore } from '@/stores/main';

/**
 * Default options for Highchart charts that should probably be used in all
 * charts.
 */

interface Dictionary<T> {
	[Key: string]: T;
}

export interface ExtendedASTNode extends ASTNode {
	id?: string;
	offset?: number;
	x1?: number;
	y1?: number;
	x2?: number;
	y2?: number;
	children?: Array<ExtendedASTNode>;
}

export interface ExtendedOptions extends Options {
	defs?: DefsOptions | Dictionary<ExtendedASTNode>;
}
export const defaultOptions = (options: ExtendedOptions): ExtendedOptions => {
	const defaults: ExtendedOptions = {
		chart: {
			styledMode: true,
		},
		title: {
			text: undefined,
		},
		credits: {
			enabled: false,
		},
		accessibility: {
			enabled: true,
		},
	};

	return merge(defaults, options);
};

/**
 * Highlight multiple series when one legend item is hovered.
 *
 * Plot lines with the same IDs as the hovered legend item series name are also
 * highlighted.
 */
export const chartHighlightSeriesOnLegendHover = ({
	/**
	 * A record where the key is the hovered series name and the value is a list
	 * of series that should also be highlighted when that series is
	 * highlighted.
	 */
	linkedSeriesToHighlight = {},
	...options
}: ChartOptions & {
	linkedSeriesToHighlight?: Record<string, string[]>;
} = {}) => {
	const highlightSeries = (hoveredSeries: string, series: Series[]) => {
		const seriesToKeepActive = [hoveredSeries].concat(
			linkedSeriesToHighlight[hoveredSeries]
		);

		const [active, inactive] = partition(series, (serie) => {
			return seriesToKeepActive.includes(serie.options.name ?? '');
		});

		active.forEach((serie) => serie.setState('hover'));
		inactive.forEach((serie) => serie.setState('inactive'));
	};

	const highlightPlotLines = (
		hoveredSeries: string,
		plotLines: PlotLineOrBand[]
	) => {
		const [activePlotLines, inactivePlotLines] = partition(
			plotLines,
			(plotLine) => plotLine.id === hoveredSeries
		);

		// Plot lines don't have states like "hover", so we need to manually
		// set the styles
		activePlotLines.forEach((plotLine) => {
			plotLine.svgElem.animate({ opacity: 1 }, { duration: 200 });
			plotLine.label.animate({ opacity: 1 }, { duration: 200 });
		});
		inactivePlotLines.forEach((plotLine) => {
			plotLine.svgElem.animate({ opacity: 0.1 }, { duration: 200 });
			plotLine.label.animate({ opacity: 0.1 }, { duration: 200 });
		});
	};

	const defaults: ChartOptions = {
		events: {
			render() {
				this.legend.allItems.forEach((item) => {
					if (
						!('legendItem' in item) ||
						!item.legendItem?.group?.element
					) {
						return;
					}

					addEvent(item.legendItem.group.element, 'mouseover', () => {
						highlightSeries(item.name, this.series);
						highlightPlotLines(
							item.name,
							this.yAxis
								.map((axis) => axis.plotLinesAndBands)
								.flat()
								.filter(isTruthy)
						);
					});

					addEvent(
						item.legendItem.group.element,
						'mouseleave',
						() => {
							this.series.forEach((serie) => {
								serie.setState('normal');
							});
							this.yAxis.forEach((axis) => {
								axis.plotLinesAndBands?.forEach((plotLine) => {
									plotLine.svgElem.animate(
										{ opacity: 1 },
										{ duration: 200 }
									);
									plotLine.label.animate(
										{ opacity: 1 },
										{ duration: 200 }
									);
								});
							});
						}
					);
				});
			},
		},
	};

	return merge(defaults, options);
};

/**
 * Disable clicking on the legend to hide and show the series.
 */
export const plotOptionsDisableClickingOnLegend = (
	options: PlotOptions
): PlotOptions => {
	const defaults: PlotOptions = {
		series: {
			events: {
				legendItemClick(e) {
					e.preventDefault();
				},
			},
		},
	};

	return merge(defaults, options);
};

/**
 * Accessibility options for charts where the Y axis is a duration. Adds an
 * accessible aria-label to data points.
 */
export const accessibilityForDuration = ({
	formatLongDuration,
	...rest
}: AccessibilityOptions & {
	formatLongDuration: ReturnType<typeof useFormatter>['formatLongDuration'];
}): AccessibilityOptions => {
	const defaults: AccessibilityOptions = {
		point: {
			descriptionFormatter: (point) => {
				const seconds = point.y ?? 0;

				return `${point.category}, ${
					point.series.name
				}: ${formatLongDuration({
					duration:
						seconds >= 3600
							? roundDurationToHours(seconds)
							: seconds,
					removeMinutesOnFullHours: true,
				})}`;
			},
		},
	};

	return merge(defaults, rest);
};

export type YAxisOptionsParams = YAxisOptions & {
	maxAxisValue?: number;
	secondsPerTick?: number;
	maximumNumberOfAxisTicks?: number;
	formatLongDuration: ReturnType<typeof useFormatter>['formatLongDuration'];
};

export type YAxisOptionsParamsFunction = (
	args: YAxisOptionsParams
) => YAxisOptions;

/**
 * Configuration for duration Y axis, rounding the duration to hours and
 * formatting it.
 */
export const yAxisDuration = ({
	maxAxisValue,
	secondsPerTick,
	formatLongDuration,
	maximumNumberOfAxisTicks = 7,
	...rest
}: YAxisOptionsParams): YAxisOptions => {
	const maxValueWithMargin =
		maxAxisValue && secondsPerTick ? maxAxisValue * 1.1 : undefined;
	// Round UP to the nearest secondsPerTick
	const max =
		maxValueWithMargin && secondsPerTick
			? Math.ceil(maxValueWithMargin / secondsPerTick) * secondsPerTick
			: undefined;

	const defaults: YAxisOptions = {
		min: 0,
		max,
		title: undefined,
		labels: {
			formatter(this: AxisLabelsFormatterContextObject) {
				return typeof this.value === 'string'
					? this.value
					: `${formatLongDuration({
							duration: roundDurationToHours(this.value),
							removeMinutesOnFullHours: true,
						})}`;
			},
		},
		tickInterval: secondsPerTick,
		/**
		 * WARNING: Incoming H A C K.
		 *
		 * We don't want the axis ticks to be too close together. Otherwise,
		 * there will be too many ticks and lines on the chart, and it will be
		 * difficult to read it. Highcharts does not have a way to configure for
		 * there to be a maximum number of ticks together with `tickInterval`.
		 * So, we need to implement the max number of ticks ourselves, while
		 * keeping the `tickInterval` setting in mind.
		 *
		 * We see if the number of ticks that Highcharts calculated is small
		 * enough, if not, then we increment the `tickInterval` and trigger
		 * calculating the tick positions again. If there are still too many
		 * ticks, then we do it again, and so on.
		 */
		tickPositioner() {
			const originalTickPositions = this.tickPositions ?? [];
			if (
				originalTickPositions.length <= maximumNumberOfAxisTicks ||
				!max ||
				!secondsPerTick
			) {
				return originalTickPositions;
			}

			this.tickInterval += secondsPerTick;
			// This updates the `this.tickPositions` internally
			this.setTickPositions();

			return this.tickPositions ?? [];
		},
	};

	return merge(defaults, rest);
};

const _yAxisLabelOptionsShared = ({
	maxAxisValue,
	secondsPerTick,
	yAxisLabelsFormatter,
	maximumNumberOfAxisTicks = 7,
	...rest
}: YAxisOptionsParams & {
	yAxisLabelsFormatter: AxisLabelsFormatterCallbackFunction;
}): YAxisOptions => {
	const maxValueWithMargin =
		maxAxisValue && secondsPerTick ? maxAxisValue * 1.1 : undefined;
	// Round UP to the nearest secondsPerTick
	const max =
		maxValueWithMargin && secondsPerTick
			? Math.ceil(maxValueWithMargin / secondsPerTick) * secondsPerTick
			: undefined;

	const defaults: YAxisOptions = {
		min: 0,
		max,
		title: undefined,
		labels: { formatter: yAxisLabelsFormatter },
		tickInterval: secondsPerTick,
		/**
		 * WARNING: Incoming H A C K.
		 *
		 * We don't want the axis ticks to be too close together. Otherwise,
		 * there will be too many ticks and lines on the chart, and it will be
		 * difficult to read it. Highcharts does not have a way to configure for
		 * there to be a maximum number of ticks together with `tickInterval`.
		 * So, we need to implement the max number of ticks ourselves, while
		 * keeping the `tickInterval` setting in mind.
		 *
		 * We see if the number of ticks that Highcharts calculated is small
		 * enough, if not, then we increment the `tickInterval` and trigger
		 * calculating the tick positions again. If there are still too many
		 * ticks, then we do it again, and so on.
		 */
		tickPositioner() {
			const originalTickPositions = this.tickPositions ?? [];
			if (
				originalTickPositions.length <= maximumNumberOfAxisTicks ||
				!max ||
				!secondsPerTick
			) {
				return originalTickPositions;
			}

			this.tickInterval += secondsPerTick;
			// This updates the `this.tickPositions` internally
			this.setTickPositions();

			return this.tickPositions ?? [];
		},
	};

	return merge(defaults, rest);
};

export const yAxisDurationRevenue = ({
	maxAxisValue,
	secondsPerTick,
	maximumNumberOfAxisTicks = 7,
	...rest
}: YAxisOptionsParams): YAxisOptions => {
	const yAxisLabelsFormatter = function (
		this: AxisLabelsFormatterContextObject
	) {
		return typeof this.value === 'string'
			? this.value
			: `${this.value * 0.001}k`;
	};
	return _yAxisLabelOptionsShared({
		maxAxisValue,
		secondsPerTick,
		yAxisLabelsFormatter,
		maximumNumberOfAxisTicks,
		...rest,
	});
};

/**
 * Treat the name of the series as a langvar and translate it in the legend.
 *
 * This also sets a couple general style rules that should work for most charts.
 */
export const legendTranslated = ({ ...options }: LegendOptions = {}) => {
	const defaults: LegendOptions = {
		labelFormatter() {
			const { t } = useMainStore().translation;
			return t(this.name);
		},
		symbolHeight: 8,
		symbolWidth: 8,
		symbolPadding: 4,
		itemDistance: 16,
	};

	return merge(defaults, options);
};

export type TooltipLine = {
	label: string;
	value: string;
	secondaryValue?: string;
	colorIndex?: number;
	type?: 'column' | 'line' | 'total' | 'gradient';
};

export type MakeTooltipValueFormatterFunction = () => (
	duration: number,
	units?: string
) => string;

const getLegendCircleSVG = () => {
	return '<rect height="100%" width="100%" class="highcharts-point" />';
};

const getLegendLineSVG = () => {
	const LEGEND_COLOR_SIZE_PX = 8;

	return `
		<path
			d="M 2 ${LEGEND_COLOR_SIZE_PX / 2} H ${LEGEND_COLOR_SIZE_PX - 2}"
			height="100%"
			width="100%"
			class="highcharts-point"
		/>`;
};

const getLegendGradientSVG = () => {
	const LEGEND_COLOR_SIZE_PX = 8;
	return `
    <svg width="${LEGEND_COLOR_SIZE_PX}" height="${LEGEND_COLOR_SIZE_PX}">
      <path
        d="M 0 0 H ${LEGEND_COLOR_SIZE_PX} V ${LEGEND_COLOR_SIZE_PX} H 0 Z"
        class="burn-up-chart-legend-icon-gradient1"
      />
    </svg>`;
};

/**
 * A helper to render a shared tooltip for all datasets.
 */
export const tooltipShared = ({
	getLinesToShow,
	getTitle,
}: {
	getLinesToShow: (
		pointsByName: Record<string, TooltipFormatterContextObject | undefined>
	) => (TooltipLine | undefined)[];
	getTitle: (x: string | number | undefined) => string;
}): TooltipOptions => ({
	followPointer: true,
	shared: true,
	useHTML: true,
	padding: 1,
	borderRadius: 0,
	shadow: false,
	borderWidth: 0,
	formatter() {
		const pointsByDatasetName =
			this.points?.reduce(
				(acc, point) => {
					acc[point.series.name] = point;
					return acc;
				},
				{} as Record<string, TooltipFormatterContextObject>
			) ?? {};

		const pointsToRender =
			getLinesToShow(pointsByDatasetName).filter(isTruthy);

		const lines = pointsToRender.map(
			({ label, value, secondaryValue, type = 'column', colorIndex }) => {
				const valueClasses = ['highcharts-shared-tooltip__value'];

				// We're trying to emulate the shapes in the legend. Use SVG
				// rather than HTML because the `highcharts-color-...` classes
				// apply SVG color attributes (fill and stroke color)
				const legendSVG =
					type === 'line' ? getLegendLineSVG() : getLegendCircleSVG();

				const labelColorClasses = [
					'highcharts-shared-tooltip__color',
					`highcharts-shared-tooltip__color--${type}`,
					`highcharts-color-${colorIndex}`,
				];
				const labelColor = legendSVG
					? `<svg class="${labelColorClasses.join(
							' '
						)}">${legendSVG}</svg>`
					: '';

				if (type === 'gradient') {
					return `
					<div class="highcharts-shared-tooltip__label">
						${getLegendGradientSVG()}
						${label}:
					</div>
					<div class="highcharts-shared-tooltip__total-value">${value}</div>
					`;
				}

				if (type === 'total') {
					return `
						<div class="highcharts-shared-tooltip__label"></div>
						<div class="highcharts-shared-tooltip__total-value">${value}</div>
					`;
				}

				return `
					<div class="highcharts-shared-tooltip__label">
						${labelColor}${label}:
					</div>
					<div class="${valueClasses.join(' ')}">${value}</div>
					<div class="highcharts-shared-tooltip__secondary-value">
						${secondaryValue ?? ''}
					</div>
				`;
			}
		);

		return `
			<div class="highcharts-tooltip__title">${getTitle(this.x)}</div>
			<div class="highcharts-shared-tooltip__items">
				${lines.join('')}
			</div>
		`;
	},
});

export const stripedPattern = {
	tagName: 'pattern',
	patternUnits: 'userSpaceOnUse',
	width: 5,
	height: 5,
	children: [
		{
			tagName: 'rect',
			width: '100%',
			height: '100%',
			fill: 'white',
		},
		{
			tagName: 'path',
			d: 'M 0 0 L 5 5 M 4.5 -0.5 L 5.5 0.5 M -0.5 4.5 L 0.5 5.5',
			'stroke-width': '2px',
		},
	],
};
